import {
  DEFAULT_SUGGESTED_AMOUNTS_COUNT,
  MAX_DONATION_FORM_FREQUENCIES,
  ProductDonationRecurrenceInterval,
  SUGGESTED_AMOUNTS_COUNT_OPTIONS,
  SuggestedAmountsCount,
} from "@simplyk/common";

import { v4 } from "uuid";

import {
  Amount,
  DefaultFrequencyValue,
  FrequencyAmountsName,
  FrequencySwitchName,
  StepSuggestedDonationAmountsFormData,
} from "../types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { SUGGESTED_AMOUNTS } from "@/common/constants/suggestedAmounts";
import { DashboardTranslationKey } from "@/public/@types/translations";

export interface DonationFrequencyConfig {
  interval: ProductDonationRecurrenceInterval;
  switchName: FrequencySwitchName;
  amountsName: FrequencyAmountsName;
  labelKey: DashboardTranslationKey;
  defaultAmounts: number[];
  amplitudeEventAdded: AmplitudeEvents;
  amplitudeEventRemoved: AmplitudeEvents;
}

const buildFrequency = (
  interval: ProductDonationRecurrenceInterval,
  config: Omit<DonationFrequencyConfig, "interval" | "switchName" | "amountsName">
): DonationFrequencyConfig => ({
  interval,
  switchName: `${interval}AmountSwitch`,
  amountsName: `${interval}Amounts`,
  ...config,
});

export const DONATION_FREQUENCIES: DonationFrequencyConfig[] = [
  buildFrequency(ProductDonationRecurrenceInterval.OneTime, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.oneTimeAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.OneTime,
    amplitudeEventAdded: AmplitudeEvents.OneTimeDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.OneTimeDonationFrequencyRemoved,
  }),
  buildFrequency(ProductDonationRecurrenceInterval.Weekly, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.weeklyAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.Weekly,
    amplitudeEventAdded: AmplitudeEvents.WeeklyDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.WeeklyDonationFrequencyRemoved,
  }),
  buildFrequency(ProductDonationRecurrenceInterval.BiWeekly, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.biWeeklyAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.BiWeekly,
    amplitudeEventAdded: AmplitudeEvents.BiWeeklyDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.BiWeeklyDonationFrequencyRemoved,
  }),
  buildFrequency(ProductDonationRecurrenceInterval.Monthly, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.monthlyAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.Monthly,
    amplitudeEventAdded: AmplitudeEvents.MonthlyDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.MonthlyDonationFrequencyRemoved,
  }),
  buildFrequency(ProductDonationRecurrenceInterval.Quarterly, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.quarterlyAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.Quarterly,
    amplitudeEventAdded: AmplitudeEvents.QuarterlyDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.QuarterlyDonationFrequencyRemoved,
  }),
  buildFrequency(ProductDonationRecurrenceInterval.Yearly, {
    labelKey: "liveFormEditor.stepSuggestedDonationAmounts.yearlyAmountSwitch",
    defaultAmounts: SUGGESTED_AMOUNTS.Yearly,
    amplitudeEventAdded: AmplitudeEvents.YearlyDonationFrequencyAdded,
    amplitudeEventRemoved: AmplitudeEvents.YearlyDonationFrequencyRemoved,
  }),
];

const DEFAULT_ENABLED_FREQUENCIES: ProductDonationRecurrenceInterval[] = [
  ProductDonationRecurrenceInterval.OneTime,
  ProductDonationRecurrenceInterval.Monthly,
];

export const isFrequencyEnabledByDefault = (interval: ProductDonationRecurrenceInterval): boolean =>
  DEFAULT_ENABLED_FREQUENCIES.includes(interval);

export const getFrequencyConfig = (interval: DefaultFrequencyValue): DonationFrequencyConfig =>
  DONATION_FREQUENCIES.find((frequency) => frequency.interval === interval) ?? DONATION_FREQUENCIES[0];

export const getEnabledFrequencies = (
  formData: Pick<StepSuggestedDonationAmountsFormData, FrequencySwitchName>
): DonationFrequencyConfig[] => DONATION_FREQUENCIES.filter((frequency) => formData[frequency.switchName]);

export const canTurnOnFrequency = (
  formData: Pick<StepSuggestedDonationAmountsFormData, FrequencySwitchName>,
  switchName: FrequencySwitchName
): boolean => formData[switchName] || getEnabledFrequencies(formData).length < MAX_DONATION_FORM_FREQUENCIES;

export const getFrequenciesShownWhenCollapsed = (
  formData: Pick<StepSuggestedDonationAmountsFormData, FrequencySwitchName>
): ProductDonationRecurrenceInterval[] => {
  const enabledIntervals = getEnabledFrequencies(formData).map((frequency) => frequency.interval);
  return enabledIntervals.length > 0 ? enabledIntervals : DEFAULT_ENABLED_FREQUENCIES;
};

export const resizeAmounts = (
  amounts: Amount[],
  interval: ProductDonationRecurrenceInterval,
  count: number
): Amount[] => {
  const suggestedAmounts = amounts.filter((amount) => (amount.amount ?? 0) > 0);
  const defaults = getFrequencyConfig(interval).defaultAmounts;
  return Array.from({ length: count }, (_, index) => {
    const existingAmount = suggestedAmounts[index];
    return existingAmount
      ? { ...existingAmount, sortIndex: index }
      : {
          id: v4(),
          amount: defaults[index] ?? null,
          message: "",
          isRecurrent: interval !== ProductDonationRecurrenceInterval.OneTime,
          sortIndex: index,
          recurrenceInterval: interval,
        };
  });
};

export const getSavedFrequencyAmounts = (
  savedAmounts: Amount[],
  interval: ProductDonationRecurrenceInterval,
  count: number
): Amount[] => {
  const suggestedAmounts = savedAmounts.filter((amount) => (amount.amount ?? 0) > 0);
  return suggestedAmounts.length > 0 && count > 0
    ? suggestedAmounts.map((amount, index) => ({ ...amount, sortIndex: index }))
    : resizeAmounts(savedAmounts, interval, count);
};

export const inferSuggestedAmountsCount = (
  savedAmounts: { amount: number | null; recurrenceInterval: ProductDonationRecurrenceInterval }[]
): SuggestedAmountsCount => {
  if (savedAmounts.length === 0) {
    return DEFAULT_SUGGESTED_AMOUNTS_COUNT;
  }
  const largestCount = Math.max(
    ...DONATION_FREQUENCIES.map(
      (frequency) =>
        savedAmounts.filter((amount) => amount.recurrenceInterval === frequency.interval && (amount.amount ?? 0) > 0)
          .length
    )
  );
  return SUGGESTED_AMOUNTS_COUNT_OPTIONS.find((option) => option >= largestCount) ?? DEFAULT_SUGGESTED_AMOUNTS_COUNT;
};
